import { useState, useEffect, useRef, useCallback } from 'react';
import moment from 'moment';

const ALL_PARTS = ['Pinya', 'Folre', 'Manilles', 'Puntals', 'Tronc', 'Organització'];

function ProvaRow({
    bundle,
    duration,
    availablePlantilles,
    visibleParts = ALL_PARTS,
    onFieldChange,
    onDurationChange,
    onPartChange,
    onAction,
    sortBy,
}) {
    const [localValues, setLocalValues] = useState({
        nom: bundle?.nom || '',
        shortName: bundle?.shortName || '',
        subtitol: bundle?.subtitol || '',
        duration: duration || '',
    });

    const debounceTimers = useRef({});
    const editingFields = useRef({});
    const [showActions, setShowActions] = useState(false);
    const actionsRef = useRef(null);

    // Sync props -> local state (only if not editing)
    useEffect(() => {
        setLocalValues(prev => {
            const newValues = { ...prev };
            ['nom', 'shortName', 'subtitol'].forEach(field => {
                if (!editingFields.current[field]) {
                    const val = bundle?.[field];
                    newValues[field] = val !== null && val !== undefined ? val : '';
                }
            });
            if (!editingFields.current.duration) {
                newValues.duration = duration || '';
            }
            return newValues;
        });
    }, [bundle?.id, bundle?.nom, bundle?.shortName, bundle?.subtitol, duration]);

    const handleTextChange = useCallback((field, value) => {
        setLocalValues(prev => ({ ...prev, [field]: value }));
        editingFields.current[field] = true;

        if (debounceTimers.current[field]) {
            clearTimeout(debounceTimers.current[field]);
        }

        debounceTimers.current[field] = setTimeout(() => {
            onFieldChange(bundle.id, field, value);
            setTimeout(() => {
                editingFields.current[field] = false;
            }, 1000);
            delete debounceTimers.current[field];
        }, 500);
    }, [bundle?.id, onFieldChange]);

    const handleDurationBlur = useCallback(() => {
        const val = parseInt(localValues.duration);
        if (!isNaN(val) && val !== duration) {
            onDurationChange(bundle.id, val);
        }
        editingFields.current.duration = false;
    }, [bundle?.id, localValues.duration, duration, onDurationChange]);

    const handleDurationKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            e.target.blur();
        }
    }, []);

    const handlePartSelect = useCallback((part, plantillaName) => {
        onPartChange(bundle.id, part, plantillaName);
    }, [bundle?.id, onPartChange]);

    // Close popover on outside click
    useEffect(() => {
        if (!showActions) return;
        const handleClick = (e) => {
            if (actionsRef.current && !actionsRef.current.contains(e.target)) {
                setShowActions(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [showActions]);

    // Cleanup timers
    useEffect(() => {
        return () => {
            Object.values(debounceTimers.current).forEach(timer => clearTimeout(timer));
        };
    }, []);

    const lastModified = bundle?.updatedAt || bundle?.createdAt;

    return (
        <tr>
            {/* Editar */}
            <td className="editor-actions-cell">
                <button
                    className="editor-edit-btn"
                    onClick={() => window.location.href = `/editor/bundle/${bundle.id}`}
                    title="Edita posicions"
                >
                    <i className="fas fa-pencil-alt"></i>
                </button>
            </td>

            {/* Nom */}
            <td className={sortBy === 'nom' ? 'sorted-column' : ''}>
                <input
                    type="text"
                    value={localValues.nom}
                    onChange={(e) => handleTextChange('nom', e.target.value)}
                    className="editor-inline-input"
                    placeholder="Nom"
                />
            </td>

            {/* Nom curt */}
            <td className={sortBy === 'shortName' ? 'sorted-column' : ''}>
                <input
                    type="text"
                    value={localValues.shortName}
                    onChange={(e) => handleTextChange('shortName', e.target.value)}
                    className="editor-inline-input input-curt"
                    placeholder="Curt"
                />
            </td>

            {/* Subtitol */}
            <td>
                <input
                    type="text"
                    value={localValues.subtitol}
                    onChange={(e) => handleTextChange('subtitol', e.target.value)}
                    className="editor-inline-input input-subtitol"
                    placeholder="—"
                />
            </td>

            {/* Duracio */}
            <td className={sortBy === 'duration' ? 'sorted-column' : ''}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <input
                        type="number"
                        value={localValues.duration}
                        onChange={(e) => {
                            editingFields.current.duration = true;
                            setLocalValues(prev => ({ ...prev, duration: e.target.value }));
                        }}
                        onBlur={handleDurationBlur}
                        onKeyDown={handleDurationKeyDown}
                        onWheel={(e) => e.target.blur()}
                        className="editor-inline-input editor-inline-input-number"
                        placeholder="—"
                        min="0"
                    />
                    <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>min</span>
                </div>
            </td>

            {/* Parts: Pinya, Folre, Manilles, Puntals, Tronc, Organitzacio */}
            {visibleParts.map(part => {
                const currentPlantilla = bundle?.parts?.[part] || '';
                const isPresent = !!currentPlantilla;
                const isInvalid = isPresent && availablePlantilles.length > 0 && !availablePlantilles.includes(currentPlantilla);
                const isSortable = part === 'Pinya' || part === 'Tronc';

                return (
                    <td key={part} className={isSortable && sortBy === part.toLowerCase() ? 'sorted-column' : ''}>
                        <select
                            value={currentPlantilla}
                            onChange={(e) => handlePartSelect(part, e.target.value)}
                            className={`editor-inline-select${!isPresent ? ' select-empty' : ''}${isInvalid ? ' select-error' : ''}`}
                            title={isInvalid ? `Plantilla "${currentPlantilla}" no existeix` : ''}
                        >
                            <option value="">—</option>
                            {availablePlantilles.map(p => (
                                <option key={p} value={p}>{p}</option>
                            ))}
                            {isInvalid ? (
                                <option value={currentPlantilla}>{currentPlantilla} (!)</option>
                            ) : null}
                        </select>
                    </td>
                );
            })}

            {/* Modificat */}
            <td className={`editor-date-cell${sortBy === 'modified' ? ' sorted-column' : ''}`}>
                {lastModified ? moment(lastModified).fromNow() : '—'}
            </td>

            {/* Accions */}
            <td className="editor-actions-cell" style={{ position: 'relative' }}>
                <button onClick={() => setShowActions(!showActions)} title="Accions">
                    &#8942;
                </button>
                {showActions ? (
                    <div ref={actionsRef} className="editor-actions-popover">
                        <button onClick={() => {
                            setShowActions(false);
                            window.location.href = `/editor/bundle/${bundle.id}`;
                        }}>
                            Editar prova
                        </button>
                        <button className="danger" onClick={() => {
                            setShowActions(false);
                            onAction('delete', bundle.id);
                        }}>
                            Eliminar
                        </button>
                    </div>
                ) : null}
            </td>
        </tr>
    );
}

export default ProvaRow;
